/*
 * Tab Recently Viewed – Custom element for rendering recently viewed
 * products inside the tab-collections section.
 *
 * Fallback collection products are server-rendered in the grid.
 * When the tab is first activated (made visible) we check for a
 * recently-viewed cookie. If the visitor has viewed products we fetch
 * them via AJAX and swap them in; otherwise the whole recently viewed
 * tab is hidden.
 *
 * Loading is deferred until the tab is visible so that lazy images
 * load correctly and the carousel can measure real dimensions.
 */
(function () {
  'use strict';

  if (customElements.get('tab-recently-viewed')) return;

  customElements.define(
    'tab-recently-viewed',
    class extends HTMLElement {
      connectedCallback() {
        this.limit = parseInt(this.dataset.limit) || 6;
        this.wrapperId = this.dataset.wrapperId;
        this.wrapper = document.getElementById(this.wrapperId);
        this.emptyState = this.querySelector('[data-recently-viewed-empty]');
        this.tab = this.closest('.tab');
        this.tabsComponent = this.closest('tabs-component');
        this.loadedNodes = [];
        this.hasInitialized = false;

        if (!this.getRecentlyViewedHandles().length || !this.wrapper) {
          this.hideRecentlyViewedTab();
          return;
        }

        if (this.tab && this.tab.classList.contains('current')) {
          this.init();
        } else {
          this.deferUntilVisible(this.tab);
        }
      }

      deferUntilVisible(tab) {
        if (!tab) {
          this.init();
          return;
        }

        this.tabObserver = new MutationObserver(() => {
          if (tab.classList.contains('current') && !this.hasInitialized) {
            this.tabObserver.disconnect();
            this.init();
          }
        });

        this.tabObserver.observe(tab, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      init() {
        if (this.hasInitialized) return;
        this.hasInitialized = true;
        this.loadProducts();
      }

      getRecentlyViewedHandles() {
        const cookieName = 'shopify_recently_viewed';
        const match = document.cookie
          .split('; ')
          .find((row) => row.startsWith(cookieName + '='));
        if (!match) return [];
        return decodeURIComponent(match.split('=')[1])
          .split(' ')
          .filter(Boolean);
      }

      async loadProducts() {
        let handles = this.getRecentlyViewedHandles();

        if (window.location.pathname.indexOf('/products/') !== -1) {
          const slug = window.location.pathname
            .split('/products/')[1]
            ?.split(/[?#]/)[0];
          if (slug) handles = handles.filter((h) => h !== slug);
        }

        if (!handles.length || !this.wrapper) {
          this.hideRecentlyViewedTab();
          return;
        }

        const toShow = handles.slice(0, this.limit);

        const results = await Promise.allSettled(
          toShow.map((handle) => this.fetchProduct(handle))
        );

        for (const result of results) {
          if (result.status === 'fulfilled' && result.value) {
            this.loadedNodes.push(...result.value);
          }
        }

        if (this.loadedNodes.length > 0) {
          this.swapToRecentlyViewed();
        } else {
          this.keepFallback();
        }
      }

      hideRecentlyViewedTab() {
        const tab = this.tab;
        const tabsComponent = this.tabsComponent;
        if (!tab || !tabsComponent) return;

        const tabIndex = tab.dataset.tab;
        const tabLink = tabsComponent.querySelector(
          `[data-tabs-link="${tabIndex}"]`
        );
        const wasCurrent = tab.classList.contains('current');

        tab.hidden = true;
        tab.setAttribute('aria-hidden', 'true');

        if (tabLink) {
          tabLink.hidden = true;
          tabLink.setAttribute('aria-hidden', 'true');
        }

        if (!wasCurrent) return;

        const nextLink = tabsComponent.querySelector('.tabs__link:not([hidden])');
        if (nextLink) {
          nextLink.dispatchEvent(new Event('click', { bubbles: true }));
        }
      }

      async fetchProduct(handle) {
        try {
          const rootUrl = window.theme?.routes?.root || '/';
          const response = await fetch(
            `${rootUrl}products/${handle}?section_id=api-product-grid-item`
          );
          if (!response.ok) return null;
          const html = await response.text();
          if (html.includes('data-unpublished')) return null;

          const aosDelay = this.loadedNodes.length * 150;
          const aosAnchor = this.wrapper.id ? `#${this.wrapper.id}` : '';
          const processed = html
            .replaceAll('||itemAosDelay||', String(aosDelay))
            .replaceAll('||itemAosAnchor||', aosAnchor);

          const temp = document.createElement('div');
          temp.innerHTML = processed;
          const content = temp.querySelector('[data-api-content]');
          if (content) return [...content.children];
        } catch (_) {
          /* skip failed products */
        }
        return null;
      }

      swapToRecentlyViewed() {
        const gridSlider = this.wrapper;
        if (!gridSlider) return;

        if (gridSlider.flkty) {
          gridSlider.flkty.destroy();
          gridSlider.flkty = null;
        }

        const fallbackItems = gridSlider.querySelectorAll(
          '[data-slide]:not(.product-grid-item--featured)'
        );
        fallbackItems.forEach((el) => el.remove());

        this.loadedNodes.forEach((node) => gridSlider.appendChild(node));

        this.activateImages();
        this.repositionQuickBuyButtons();
        this.triggerSliderInit();
      }

      keepFallback() {
        const hasProducts = this.wrapper
          ? this.wrapper.querySelectorAll(
              '[data-slide]:not(.product-grid-item--featured)'
            ).length > 0
          : false;

        if (!hasProducts) {
          if (this.emptyState) this.emptyState.style.display = '';
          return;
        }

        this.activateImages();
        this.repositionQuickBuyButtons();
        this.triggerSliderInit();
      }

      activateImages() {
        if (!this.wrapper) return;

        this.wrapper
          .querySelectorAll('img[loading="lazy"]')
          .forEach((img) => {
            img.loading = 'eager';
          });

        this.wrapper
          .querySelectorAll('img.is-loading')
          .forEach((img) => {
            if (img.complete && img.naturalWidth > 0) {
              img.classList.remove('is-loading');
              if (img.parentNode) {
                img.parentNode.classList.remove('is-loading');
              }
            }
          });
      }

      repositionQuickBuyButtons() {
        const section = this.closest('.tab-collections');
        if (!section || !section.classList.contains('tab-collections--show-atc')) return;
        if (!this.wrapper) return;

        this.wrapper
          .querySelectorAll('.product-grid-item__quick-buy')
          .forEach((btn) => {
            const card = btn.closest('.product-grid-item');
            if (card && btn.parentElement !== card) {
              card.appendChild(btn);
            }
          });
      }

      triggerSliderInit() {
        requestAnimationFrame(() => {
          const gridSlider = this.wrapper;
          if (gridSlider && typeof gridSlider.checkSlidesSize === 'function') {
            gridSlider.checkSlidesSize();
          } else {
            document.dispatchEvent(new CustomEvent('theme:resize:width'));
          }

          const tabsComponent = this.closest('tabs-component');
          if (tabsComponent) {
            const activeLink = tabsComponent.querySelector(
              '.tabs__link.current'
            );
            if (activeLink) {
              activeLink.dispatchEvent(
                new CustomEvent('theme:custom-scrollbar:change', {
                  bubbles: true,
                  detail: { element: activeLink },
                })
              );
            }
          }
        });
      }

      disconnectedCallback() {
        if (this.tabObserver) {
          this.tabObserver.disconnect();
        }
      }
    }
  );
})();
